import * as React from "react";
import { observer } from "mobx-react";
import { scheduleGenerationEditorState } from "../store/store";
import { setIsAutoSchedulePanelOpen, persistAllShiftBlocks, hasUnsavedShiftBlockChanges, resetScheduleGenerationEditorState } from "../mutatorActions/actions";
import { FocusTrapZone, IContextualMenuItem } from "@fluentui/react";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import {
    Button as FluentButton,
    OptionOnSelectData,
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverCarousel,
    TeachingPopoverCarouselCard,
    TeachingPopoverCarouselFooter,
    TeachingPopoverCarouselNav,
    TeachingPopoverCarouselNavButton,
    TeachingPopoverSurface,
    TeachingPopoverTitle
} from "@fluentui/react-components";
import type { RadioGroupProps } from "@fluentui/react-components";
import type { TeachingPopoverCarouselFooterButtonProps } from "@fluentui/react-teaching-popover";
import type { OnOpenChangeData, OpenPopoverEvents } from "@fluentui/react-popover";
import { Subtitle1, Subtitle2, Link, FluentProvider, Divider, useId, Body1, Field, mergeClasses, Option, Tooltip, Combobox } from "@microsoft/shifts-ui-core";
import { NewShiftsDialog } from "../../common/NewShiftsDialog/NewShiftsDialog";
import { ShiftsDialog } from "../../common/ShiftsDialog/ShiftsDialog";
import StringsStore from "../../../../sh-strings/store";
import ShiftUtils from "sh-application/utility/ShiftUtils";
import DateUtils from "sh-application/utility/DateUtils";
import { TeamStore } from "sh-team-store";
import { Add20Regular, OpenRegular } from "@fluentui/react-icons";
import { IShiftEditorData, launchShiftEditor, shiftEditorViewState } from "../../shift/lib";
import { TagStore } from "sh-stores/sh-tag-store";
import TagUtils from "../../../utility/TagUtils";
import { ITagEntity, ScheduleCalendarTypes, ShiftTypes } from "../../../../sh-models";
import ShiftBlock from "../shiftBlock/ShiftBlock";
import * as moment from "moment";
import type { Moment } from "moment";
import * as Sticky from "react-stickynode";
import { setFREsShownSoFar, hasSeenFRE } from "sh-stores";
import { UserFRETypes } from "sh-models";
import { InstrumentationService, TeamDataService, ECSConfigService, ECSConfigKey, SmartScheduleDataService } from "sh-services";
import SmartScheduleGenerationPollingService from "sh-services/lib/SmartScheduleGenerationPollingService";
import MemberUtils from "sh-application/utility/MemberUtils";
import { updateSmartSchedulePollingStatus } from "../mutatorActions/pollingActions";
import { JobStatus } from "sh-models/job/SmartScheduleGenerationJobStatusEntity";
import { IGenerateScheduleRequest, IGenerateScheduleDayTemplate } from "sh-rest-client/GenerateScheduleRequest";
import { WeekDays } from "sh-models";
import UrlFactory from "../../../utility/UrlFactory";
import { useHistory } from "react-router-dom";
import setGlobalCommandBarSelectedTab from "../../../store/mutatorActions/setGlobalCommandBarSelectedTab";
import { GlobalCommandBarTab } from "../../../store/schema/GlobalCommandBarViewState";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { schedulingConstraintsPanelActions, SchedulingConstraintsPanelStore } from "@microsoft/shifts-states-schedules";
import { SchedulingRulesPanel, CallbacksProvider } from "@microsoft/shifts-experiences-smart-scheduling";
import { computeSmartScheduleBoundaries, GenerateOptions } from "../smartScheduleUtils";
import { useScheduleEditorStyles } from "./ScheduleGenerationEditorPage.style";
import { onSelectSmartScheduleGroup } from "../orchestrators/onSelectSmartScheduleGroup";
import setSmartScheduleSelectedGroup from "../actions/setSmartScheduleSelectedGroup";
import { useShiftBlockKeyboardNavigation, useScrollToAddButton } from "./hooks";
import ScheduleGenerationEditorPageHeader from "./ScheduleGenerationEditorPageHeader";
import { ExistingShiftsDialog } from "./ExistingShiftsDialog";
import { generateDomPropertiesForAria } from "owa-accessibility";
import LocalizedDatePickerV2 from "../../common/LocalizedDatePickerV2";
import { TeamSettingsStore } from "../../../../sh-stores/sh-teamsettings-store";
import exportMatchShiftsReport from "../../schedules/lib/actions/exportMatchShiftsReport";
import { STICKY_Z_INDEX } from "../../../../StaffHubConstants";

const styles = require("./ScheduleGenerationEditorPage.scss");

enum TeachingCarouselStep {
    DateRange = "dateRangeStep",
    SchedulingRules = "schedulingRulesStep",
    ShiftBlocks = "shiftBlocksStep"
}

export interface ExecuteScheduleGenerationPreflightParams {
    teamId: string;
    tagId: string;
    generateOption: string;
    startDate: any;
    endDate: any;
    getStartOfDay: (date: any) => Moment;
    getEndOfDayForDate: (date: any) => Moment;
    exportMatchShiftsReport: typeof exportMatchShiftsReport;
    updateSmartSchedulePollingStatus: typeof updateSmartSchedulePollingStatus;
    getCurrentUserMemberId: () => string;
}

export function executeScheduleGenerationPreflight(params: ExecuteScheduleGenerationPreflightParams): void {
    const {
        teamId,
        tagId,
        generateOption,
        startDate,
        endDate,
        getStartOfDay,
        getEndOfDayForDate,
        exportMatchShiftsReport,
        updateSmartSchedulePollingStatus,
        getCurrentUserMemberId
    } = params;

    // When replacing existing shifts, automatically trigger time report export
    if (generateOption === GenerateOptions.REPLACE) {
        const startOfStartDate = getStartOfDay(startDate);
        const endOfEndDate = getEndOfDayForDate(endDate);
        exportMatchShiftsReport("", startOfStartDate, endOfEndDate, "SmartSchedulingExistingShiftsDialog");
    }

    // Optimistically set polling status to InProgress so UI disables immediately.
    const currentMemberId = getCurrentUserMemberId() || "";
    updateSmartSchedulePollingStatus({
        jobId: teamId,
        teamId,
        tagId,
        memberId: currentMemberId,
        status: JobStatus.InProgress,
        result: {
            tagId: tagId,
            memberId: currentMemberId
        },
        jobProperties: {
            overrideExistingSchedule: generateOption === GenerateOptions.REPLACE,
            isReviewed: false
        }
    });
}

const ShiftGenerationEditorPage: React.FC = () => {
    const history = useHistory();
    const viewSchedulingRulesLinkId = useId('viewSchedulingRulesLink');
    const addShiftBlockButtonId = useId('addShiftBlockButton');
    const viewSchedulingRulesLinkRef = React.useRef<HTMLAnchorElement>(null);
    const endDatePickerFieldRef = React.useRef<HTMLDivElement | null>(null);
    const saveAndCloseButtonRef = React.useRef<HTMLButtonElement>(null);
    const beginOnboardingPrimaryButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const strings = StringsStore().registeredStringModules.get("smartSchedule").strings;
    const commonStrings = StringsStore().registeredStringModules.get("common").strings;
    const scheduleStrings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const teamSetupPickerStrings = StringsStore().registeredStringModules.get("teamSetupPicker").strings;
    const teamSetupStartTourStrings = StringsStore().registeredStringModules.get("teamSetupStartTour").strings;
    const currentLocale = StringsStore().currentLocale;
    const isCurrentUserAdmin: boolean = MemberUtils.isAdmin(TeamStore() && TeamStore().me);

    const shiftBlocks = scheduleGenerationEditorState().shiftBlocks || [];
    const [comboboxInputValue, setComboboxInputValue] = React.useState<string>("");
    const [isUserTyping, setIsUserTyping] = React.useState<boolean>(false);

    // TeachingPopover visibility state: only one visible at a time
    const smartSchedulingMadeEasySeen = hasSeenFRE(UserFRETypes.SmartSchedulingMadeEasy);
    const step0Seen = hasSeenFRE(UserFRETypes.SmartSchedulingIntroStep0);
    const step1Seen = hasSeenFRE(UserFRETypes.SmartSchedulingIntroStep1);
    const step2Seen = hasSeenFRE(UserFRETypes.SmartSchedulingIntroStep2);
    const [showBeginOnboardingDialog, setShowBeginOnboardingDialog] = React.useState<boolean>(() =>
        isCurrentUserAdmin &&
        ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) &&
        !smartSchedulingMadeEasySeen
    );
    // If earlier steps not seen -> show them in order (DateRange -> SchedulingRules -> ShiftBlocks)
    const [showDateRangeBubble, setShowDateRangeBubble] = React.useState<boolean>(() => !step0Seen);
    const [showSchedulingRulesBubble, setShowSchedulingRulesBubble] = React.useState<boolean>(() => step0Seen && !step1Seen);
    const [showShiftBlockBubble, setShowShiftBlockBubble] = React.useState<boolean>(() => step1Seen && !step2Seen);
    // Dialog state for Generate Schedule confirmation
    const [showExistingShiftsDialog, setShowExistingShiftsDialog] = React.useState<boolean>(false);
    const [generateOption, setGenerateOption] = React.useState<string>(GenerateOptions.CONSIDER);
    const [existingShiftsCount, setExistingShiftsCount] = React.useState<number>(0);
    // Dialog states for validation errors
    const [showNoGroupDialog, setShowNoGroupDialog] = React.useState<boolean>(false);
    const [showEmptyGroupDialog, setShowEmptyGroupDialog] = React.useState<boolean>(false);
    const [showNoShiftBlocksDialog, setShowNoShiftBlocksDialog] = React.useState<boolean>(false);
    // Dialog state for unsaved changes confirmation
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = React.useState<boolean>(false);
    // State to control the Generate button's enabled/disabled state
    const [isGenerating, setIsGenerating] = React.useState<boolean>(false);
    const pageStyles = useScheduleEditorStyles();
    const addShiftBlockButtonRef = React.useRef<HTMLButtonElement | null>(null);
    const initialTeachingStep: TeachingCarouselStep = showDateRangeBubble
        ? TeachingCarouselStep.DateRange
        : showSchedulingRulesBubble
            ? TeachingCarouselStep.SchedulingRules
            : TeachingCarouselStep.ShiftBlocks;
    const [teachingCarouselValue, setTeachingCarouselValue] = React.useState<TeachingCarouselStep>(initialTeachingStep);
    const previousTeachingStepRef = React.useRef<TeachingCarouselStep>(initialTeachingStep);
    const hasRecordedStep0Ref = React.useRef<boolean>(step0Seen);
    const hasRecordedStep1Ref = React.useRef<boolean>(step1Seen);
    const hasRecordedStep2Ref = React.useRef<boolean>(step2Seen);
    const hasLoggedBeginOnboardingDialogShownRef = React.useRef<boolean>(false);

    const editOrDeleteFRESeen = hasSeenFRE(UserFRETypes.EditOrDeleteShifts);
    const shiftRecurrenceFRESeen = hasSeenFRE(UserFRETypes.ShiftRecurrence);
    const [showShiftBlocksTeachingPopover, setShowShiftBlocksTeachingPopover] = React.useState<boolean>(false);
    const hasShownShiftBlocksTeachingRef = React.useRef<boolean>(false);
    const previousShiftBlockCountRef = React.useRef<number>(shiftBlocks.length);

    const shouldShowTeachingPopover =
        isCurrentUserAdmin &&
        ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) &&
        !showBeginOnboardingDialog &&
        (showDateRangeBubble || showSchedulingRulesBubble || showShiftBlockBubble);
    const teachingPopoverTarget = teachingCarouselValue === TeachingCarouselStep.DateRange
        ? endDatePickerFieldRef.current
        : teachingCarouselValue === TeachingCarouselStep.SchedulingRules
            ? viewSchedulingRulesLinkRef.current
            : addShiftBlockButtonRef.current;

    React.useEffect(() => {
        const currentCount = shiftBlocks.length;
        const previousCount = previousShiftBlockCountRef.current;

        if (
            !hasShownShiftBlocksTeachingRef.current &&
            previousCount === 0 &&
            currentCount > 0 &&
            isCurrentUserAdmin &&
            ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) &&
            !shouldShowTeachingPopover &&
            (!editOrDeleteFRESeen || !shiftRecurrenceFRESeen)
        ) {
            setShowShiftBlocksTeachingPopover(true);
            hasShownShiftBlocksTeachingRef.current = true;
        }

        previousShiftBlockCountRef.current = currentCount;
    }, [shiftBlocks.length, isCurrentUserAdmin, editOrDeleteFRESeen, shiftRecurrenceFRESeen, shouldShowTeachingPopover]);

    // Scroll the "Add Shift Block" button into view when a new shift block is added
    useScrollToAddButton(shiftBlocks.length, addShiftBlockButtonRef);

    const handleTeachingPopoverOpenChange = React.useCallback((_: OpenPopoverEvents, data: OnOpenChangeData) => {
        if (!data.open) {
            setShowDateRangeBubble(false);
            setShowSchedulingRulesBubble(false);
            setShowShiftBlockBubble(false);
            setTeachingCarouselValue(TeachingCarouselStep.DateRange);
            previousTeachingStepRef.current = TeachingCarouselStep.DateRange;
        }
    }, []);

    const moveToShiftBlockStep = React.useCallback(async () => {
        if (teachingCarouselValue === TeachingCarouselStep.ShiftBlocks) {
            return;
        }

        if (teachingCarouselValue === TeachingCarouselStep.SchedulingRules) {
            // Next from Scheduling Rules to Shift Blocks
            InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreSchedulingRulesNextClicked);
        }

        previousTeachingStepRef.current = TeachingCarouselStep.ShiftBlocks;
        setTeachingCarouselValue(TeachingCarouselStep.ShiftBlocks);
        setShowDateRangeBubble(false);
        setShowSchedulingRulesBubble(false);
        setShowShiftBlockBubble(true);

        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingRulesNextClicked, []);

        if (!hasRecordedStep1Ref.current) {
            try {
                await setFREsShownSoFar([UserFRETypes.SmartSchedulingIntroStep1]);
                hasRecordedStep1Ref.current = true;
            } catch {
                // Swallow telemetry persistence errors
            }
        }
    }, [teachingCarouselValue]);

    const moveToSchedulingRulesStep = React.useCallback(async () => {
        if (teachingCarouselValue === TeachingCarouselStep.SchedulingRules) {
            return;
        }

        if (teachingCarouselValue === TeachingCarouselStep.DateRange) {
            // Next from Date Range to Scheduling Rules
            InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreDateRangeNextClicked);
        } else if (teachingCarouselValue === TeachingCarouselStep.ShiftBlocks) {
            // Back from Shift Blocks to Scheduling Rules
            InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreShiftBlocksBackClicked);
        }

        previousTeachingStepRef.current = TeachingCarouselStep.SchedulingRules;
        setTeachingCarouselValue(TeachingCarouselStep.SchedulingRules);
        setShowDateRangeBubble(false);
        setShowShiftBlockBubble(false);
        setShowSchedulingRulesBubble(true);
        if (teachingCarouselValue === TeachingCarouselStep.ShiftBlocks) {
            InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingShiftBlockBackClicked, []);
        }

        if (teachingCarouselValue === TeachingCarouselStep.DateRange && !hasRecordedStep0Ref.current) {
            try {
                await setFREsShownSoFar([UserFRETypes.SmartSchedulingIntroStep0]);
                hasRecordedStep0Ref.current = true;
            } catch {
                // Swallow telemetry persistence errors
            }
        }
    }, [teachingCarouselValue]);

    const moveToDateRangeStep = React.useCallback(() => {
        if (teachingCarouselValue === TeachingCarouselStep.DateRange) {
            return;
        }

        if (teachingCarouselValue === TeachingCarouselStep.SchedulingRules) {
            // Back from Scheduling Rules to Date Range
            InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreSchedulingRulesBackClicked);
        }

        previousTeachingStepRef.current = TeachingCarouselStep.DateRange;
        setTeachingCarouselValue(TeachingCarouselStep.DateRange);
        setShowDateRangeBubble(true);
        setShowSchedulingRulesBubble(false);
        setShowShiftBlockBubble(false);
    }, [teachingCarouselValue]);

    const completeTeachingSteps = React.useCallback(async () => {
        // Got it on Shift Blocks step
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreShiftBlocksGotItClicked);

        previousTeachingStepRef.current = TeachingCarouselStep.SchedulingRules;
        setShowDateRangeBubble(false);
        setShowShiftBlockBubble(false);
        setShowSchedulingRulesBubble(false);
        setTeachingCarouselValue(TeachingCarouselStep.SchedulingRules);

        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingShiftBlockGotItClicked, []);

        if (!hasRecordedStep2Ref.current) {
            try {
                await setFREsShownSoFar([UserFRETypes.SmartSchedulingIntroStep2]);
                hasRecordedStep2Ref.current = true;
            } catch {
                // Swallow telemetry persistence errors
            }
        }
    }, []);

    const handleBeginOnboardingSkip = React.useCallback(async () => {
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreBeginOnboardingSkipClicked);
        setShowBeginOnboardingDialog(false);
        setShowDateRangeBubble(false);
        setShowSchedulingRulesBubble(false);
        setShowShiftBlockBubble(false);

        try {
            await setFREsShownSoFar([
                UserFRETypes.SmartSchedulingMadeEasy,
                UserFRETypes.SmartSchedulingIntroStep0,
                UserFRETypes.SmartSchedulingIntroStep1,
                UserFRETypes.SmartSchedulingIntroStep2
            ]);
            hasRecordedStep0Ref.current = true;
            hasRecordedStep1Ref.current = true;
            hasRecordedStep2Ref.current = true;
        } catch {
            // Swallow telemetry persistence errors
        }
    }, []);

    const handleBeginOnboardingPrimaryClick = React.useCallback(async () => {
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreBeginOnboardingPrimaryClicked);
        setShowBeginOnboardingDialog(false);
        try {
            await setFREsShownSoFar([UserFRETypes.SmartSchedulingMadeEasy]);
        } catch {
            // Swallow telemetry persistence errors
        }
    }, []);

    const handleTeachingCarouselValueChange = React.useCallback((_: unknown, data: { value?: string }) => {
        if (!data.value) {
            return;
        }

        switch (data.value) {
            case TeachingCarouselStep.ShiftBlocks:
                moveToShiftBlockStep();
                break;
            case TeachingCarouselStep.SchedulingRules:
                moveToSchedulingRulesStep();
                break;
            case TeachingCarouselStep.DateRange:
                moveToDateRangeStep();
                break;
            default:
                break;
        }
    }, [moveToDateRangeStep, moveToSchedulingRulesStep, moveToShiftBlockStep]);

    const handleShiftBlocksTeachingPopoverDismiss = React.useCallback(() => {
        setShowShiftBlocksTeachingPopover(false);
    }, []);

    // Track previous panel open state for focus restoration
    const wasPanelOpenRef = React.useRef<boolean>(false);

    const closePanel = React.useCallback(() => {
        // Check if there are unsaved changes
         InstrumentationService.logEvent(
            InstrumentationService.events.SmartSchedulingPanelCancelClicked
          );
        if (hasUnsavedShiftBlockChanges()) {
            setShowUnsavedChangesDialog(true);
        } else {
            setIsAutoSchedulePanelOpen(false);
        }
    }, []);

    const handleDiscardAndClose = React.useCallback(() => {
        const state = scheduleGenerationEditorState();
        const shiftBlocks = state.shiftBlocks || [];
        const existingShiftBlocks  = shiftBlocks.filter(shift => shift.eTag !== undefined && shift.eTag !== null);
        const newShiftBlocks = shiftBlocks.filter(shift => shift.eTag === undefined || shift.eTag === null);
        const tagId = state.selectedGroupId;
        InstrumentationService.logEvent(
            InstrumentationService.events.SmartSchedulingPanelPopupDiscardEditsClicked,
            [
                getGenericEventPropertiesObject(InstrumentationService.properties.ShiftBlocksCount, existingShiftBlocks.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.NewShiftBlocksToSaveOrDiscardCount, newShiftBlocks.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, tagId)
            ]
        );
        setShowUnsavedChangesDialog(false);
        setIsAutoSchedulePanelOpen(false);
    }, []);

    const handleSaveChangesAndClose = React.useCallback(() => {
        const state = scheduleGenerationEditorState();
        const shiftBlocks = state.shiftBlocks || [];
        const existingShiftBlocks  = shiftBlocks.filter(shift => shift.eTag !== undefined && shift.eTag !== null);
        const newShiftBlocks = shiftBlocks.filter(shift => shift.eTag === undefined || shift.eTag === null);
        const tagId = state.selectedGroupId;
        InstrumentationService.logEvent(
            InstrumentationService.events.SmartSchedulingPanelPopupSaveClicked,
            [
                getGenericEventPropertiesObject(InstrumentationService.properties.ShiftBlocksCount, existingShiftBlocks.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.NewShiftBlocksToSaveOrDiscardCount, newShiftBlocks.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, tagId)
            ]
        );
        setShowUnsavedChangesDialog(false);
        persistAllShiftBlocks();
        setIsAutoSchedulePanelOpen(false);
    }, []);

    const handleSaveAndClose = React.useCallback(() => {
        const state = scheduleGenerationEditorState();
        const shiftBlocks = state.shiftBlocks || [];
        const existingShiftBlocks  = shiftBlocks.filter(shift => shift.eTag !== undefined && shift.eTag !== null);
        const newShiftBlocks = shiftBlocks.filter(shift => shift.eTag === undefined || shift.eTag === null);
        const tagId = state.selectedGroupId;
        InstrumentationService.logEvent(
        InstrumentationService.events.SmartSchedulingPanelSaveandCloseClicked,
            [
                getGenericEventPropertiesObject(InstrumentationService.properties.ShiftBlocksCount, existingShiftBlocks.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.NewShiftBlocksToSaveOrDiscardCount, newShiftBlocks.length),
                getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, tagId)
            ]
        );
        persistAllShiftBlocks();
        setIsAutoSchedulePanelOpen(false);
    }, []);

    // Reset store and isGenerating state when panel opens
    React.useEffect(() => {
        if (scheduleGenerationEditorState().isAutoSchedulePanelOpen) {
            resetScheduleGenerationEditorState();
            setIsGenerating(false);
        }
    }, [scheduleGenerationEditorState().isAutoSchedulePanelOpen]);

    // Auto-select the first tag option when panel opens
    React.useEffect(() => {
        if (scheduleGenerationEditorState().isAutoSchedulePanelOpen && !scheduleGenerationEditorState().selectedGroupId) {
            const tags: ITagEntity[] = [];
            TagStore().tags.forEach((tag: ITagEntity) => {
                if (TagUtils.isActiveTag(tag) && !TagUtils.isDefaultTag(tag.id)) {
                    tags.push(tag);
                }
            });

            if (tags.length > 0) {
                // Auto-select the first available tag
                setSmartScheduleSelectedGroup(tags[0].id);
            }
        }
    }, [scheduleGenerationEditorState().isAutoSchedulePanelOpen]);

    // Set focus to the save and close button when the panel opens (for keyboard accessibility)
    React.useEffect(() => {
        if (scheduleGenerationEditorState().isAutoSchedulePanelOpen) {
            // Use setTimeout to ensure the panel is fully rendered before focusing
            const timeoutId = setTimeout(() => {
                if (saveAndCloseButtonRef.current) {
                    saveAndCloseButtonRef.current.focus();
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [scheduleGenerationEditorState().isAutoSchedulePanelOpen]);

    // Set focus to the Begin Onboarding primary button when the dialog opens
    React.useEffect(() => {
        if (!showBeginOnboardingDialog) {
            return;
        }

        const timeoutId = setTimeout(() => {
            if (beginOnboardingPrimaryButtonRef.current) {
                beginOnboardingPrimaryButtonRef.current.focus();
            }
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [showBeginOnboardingDialog]);

    // Restore focus to the "View scheduling rules" link when the scheduling constraints panel closes
    React.useEffect(() => {
        const schedulingConstraintsPanelStore = SchedulingConstraintsPanelStore();
        const isPanelCurrentlyOpen = schedulingConstraintsPanelStore.isPanelOpen;

        // Track previous panel open state and restore focus when it transitions from open to closed
        if (wasPanelOpenRef.current && !isPanelCurrentlyOpen) {
            // Panel just closed - restore focus to the link
            if (viewSchedulingRulesLinkRef.current) {
                viewSchedulingRulesLinkRef.current.focus();
            }
        }

        // Update the previous state
        wasPanelOpenRef.current = isPanelCurrentlyOpen;
    }, [SchedulingConstraintsPanelStore().isPanelOpen]);

    // Global messages are now handled in Schedules.tsx based on polling state;
    // this component only updates polling status.

    const updateFailureStatus = React.useCallback((teamId: string | undefined, tagId: string | undefined, code: string, message: string, start?: string, end?: string) => {
        if (!teamId) { return; }
        const currentMemberId = MemberUtils.getCurrentUserMemberId() || "";
        updateSmartSchedulePollingStatus({
            jobId: teamId,
            teamId,
            tagId,
            memberId: currentMemberId,
            status: JobStatus.Failure,
            result: {
                teamId,
                tagId: tagId || "",
                jobId: teamId,
                memberId: currentMemberId,
                createdShiftIds: null,
                createdOpenShiftIds: null,
                error: { code, message }
            },
            jobProperties: {
                overrideExistingSchedule: generateOption === GenerateOptions.REPLACE,
                isReviewed: false,
                startDate: start,
                endDate: end
            }
        });
    }, [generateOption]);

    // Move date state declarations above executeScheduleGeneration to avoid 'used before declaration'
    const today = moment();
    const [startDate, setStartDate] = React.useState<any>(today);
    const [endDate, setEndDate] = React.useState<any>(today.clone().add(7, 'days'));
    const todayDate = React.useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0); // Normalize to start of day
        return now;
    }, []);
    // Handler for start date changes with validation
    const handleStartDateChange = React.useCallback((newStartDateParam: Date | null) => {
        if (!newStartDateParam || isNaN(newStartDateParam.getTime())) {
            return;
        }
        const newStartDate = moment(newStartDateParam);
        setStartDate(newStartDate);
        // Check if end date is before the new start date
        if (endDate.isBefore(newStartDate, 'day')) {
            // Automatically adjust end date to match the start date
            setEndDate(moment(newStartDate));
        }
        // Check if end date is more than 3 weeks from new start date
        const maxEndDate = moment(newStartDate).add(3, 'weeks');
        if (endDate.isAfter(maxEndDate)) {
            // Automatically adjust end date to be exactly 3 weeks from start date
            setEndDate(maxEndDate);
        }
    }, [endDate]);

    // Handler for end date changes with validation
    const handleEndDateChange = React.useCallback((newEndDateParam: Date | null) => {
        if (!newEndDateParam || isNaN(newEndDateParam.getTime())) {
            return;
        }
        const newEndDate = moment(newEndDateParam);
        // Check if new end date is before the start date
        if (moment(newEndDate).isBefore(startDate, 'day')) {
            // Automatically adjust start date to match the new end date
            setStartDate(moment(newEndDate));
        }
        // Check if new end date is more than 3 weeks from start date
        const maxEndDate = moment(startDate).add(3, 'weeks');
        if (moment(newEndDate).isAfter(maxEndDate)) {
            // Automatically adjust start date to be 3 weeks before the new end date
            const newStartDate = moment(newEndDate).subtract(3, 'weeks');
            setStartDate(newStartDate);
        }
        setEndDate(newEndDate);
    }, [startDate]);

    const executeScheduleGeneration = React.useCallback(async () => {
        // Close the dialog and panel
        setShowExistingShiftsDialog(false);
        setIsAutoSchedulePanelOpen(false);
        const tagId = scheduleGenerationEditorState().selectedGroupId;
        const teamId = TeamStore().teamId;
        if (!teamId || !tagId) {
            updateFailureStatus(teamId, tagId, "InvalidInput", "Missing teamId or tagId");
            return;
        }

        executeScheduleGenerationPreflight({
            teamId,
            tagId,
            generateOption,
            startDate,
            endDate,
            getStartOfDay: DateUtils.getStartOfDay,
            getEndOfDayForDate: DateUtils.getEndOfDayForDate,
            exportMatchShiftsReport,
            updateSmartSchedulePollingStatus,
            getCurrentUserMemberId: MemberUtils.getCurrentUserMemberId
        });

        try {
            // Build generate schedule request from current editor state
            const overrideExistingSchedule = generateOption === GenerateOptions.REPLACE;
            const shiftBlocks = scheduleGenerationEditorState().shiftBlocks;

            // Compute UTC datetime boundaries via shared utility (returns full ISO datetimes)
            const teamTimezone = TeamSettingsStore().timeZoneOlsonCode.value;
            const { startDateBoundaryUtc: startDateBoundaryUtc, endDateBoundaryUtc: endDateBoundaryUtc } = computeSmartScheduleBoundaries(shiftBlocks, startDate, endDate, teamTimezone);
            // For IGenerateScheduleRequest we only send date portion (YYYY-MM-DD) per latest requirement
            const startDateStr = moment(startDateBoundaryUtc).utc().format("YYYY-MM-DD");
            const endDateStr = moment(endDateBoundaryUtc).utc().format("YYYY-MM-DD");

            const allWeekDays: string[] = [
                WeekDays.Monday,
                WeekDays.Tuesday,
                WeekDays.Wednesday,
                WeekDays.Thursday,
                WeekDays.Friday,
                WeekDays.Saturday,
                WeekDays.Sunday
            ];
            const dayTemplates: IGenerateScheduleDayTemplate[] = shiftBlocks.map(shift => {
                // Convert times to UTC for transmission
                const startTimeStr = moment.tz(shift.startTime, TeamSettingsStore().timeZoneOlsonCode.value).utc().format("HH:mm:ss");
                const endTimeStr = moment.tz(shift.endTime, TeamSettingsStore().timeZoneOlsonCode.value).utc().format("HH:mm:ss");
                const repeatEveryday = shift.repeatOn && shift.repeatOn.length === 7; // all days selected
                const notes = shift.notes;
                const title = shift.title;

                const repeatDays: string[] = (shift.repeatOn && shift.repeatOn.length) ? shift.repeatOn as string[] : allWeekDays;
                return {
                    id: shift.id,
                    eTag: shift.eTag,
                    title,
                    startTime: startTimeStr,
                    endTime: endTimeStr,
                    startTimeLocal:  moment(shift.startTime).tz(TeamSettingsStore().timeZoneOlsonCode.value).format("HH:mm:ss"),
                    endTimeLocal: moment(shift.endTime).tz(TeamSettingsStore().timeZoneOlsonCode.value).format("HH:mm:ss"),
                    slots: String(shift.openSlots || 0),
                    repeatEveryday,
                    repeatDays, // always include for server requirement
                    notes,
                    theme: shift.theme,
                    breaks: (shift.breaks || []).map(brk => ({
                        duration: brk.duration,
                        breakType: brk.breakType
                    })),
                    subshifts: (shift.subshifts || []).map(sub => ({
                        title: sub.title || "",
                        startTime: moment(sub.startTime).utc().format("HH:mm:ss"),
                        endTime: moment(sub.endTime).utc().format("HH:mm:ss"),
                        theme: sub.theme,
                        subshiftType: sub.subshiftType
                    }))
                };
            });

            const request: IGenerateScheduleRequest = {
                startDate: startDateStr, // date-only (UTC) boundary
                endDate: endDateStr,     // date-only (UTC) boundary
                startDateLocal: moment(startDate).format("YYYY-MM-DD"),
                endDateLocal: moment(endDate).format("YYYY-MM-DD"),
                overrideExistingSchedule,
                shiftsPerDay: dayTemplates
            };

            // Fire-and-forget API call; polling will surface completion status
            SmartScheduleDataService.generateScheduleForTag(teamId, tagId, request)
                .catch((e: any) => {
                    // Use the date-only boundaries in failure status as well
                    updateFailureStatus(teamId, tagId, "GenerationRequestFailed", e?.message || "request failed", startDateStr, endDateStr);
                });
        } catch (e: any) {
            // On build failure fall back to user selected date-only values
            updateFailureStatus(teamId, tagId, "GenerationBuildFailed", e?.message || "exception building request", moment(startDate).utc().format("YYYY-MM-DD"), moment(endDate).utc().format("YYYY-MM-DD"));
        }
    }, [generateOption, startDate, endDate, strings, updateFailureStatus]);

    const onViewSchedulingRulesClick = React.useCallback(() => {
        // Log user interaction for telemetry
        InstrumentationService.logEvent(InstrumentationService.events.ScheduleNavigationToView, [
            getGenericEventPropertiesObject(
                InstrumentationService.properties.CurrentView,
                "SmartSchedulingPanel"
            )
        ]);

        // Open the scheduling constraints panel
        schedulingConstraintsPanelActions.openPanel();
    }, []);

    const _getTagOptions = (): IContextualMenuItem[] => {
        const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;
        let tagOptions: IContextualMenuItem[] = [];
        TagStore().tags.forEach((tag: ITagEntity) => {
            if (TagUtils.isActiveTag(tag) && !TagUtils.isDefaultTag(tag.id)) {
                const name = tag.name || commonStrings.get("unnamedGroup");
                const isEmpty = TagUtils.isTagEmpty(tag.id);
                tagOptions.push({
                    key: tag.id,
                    name: name,
                    text: name,
                    checked: selectedGroupId === tag.id,
                    ariaLabel: name,
                    title: name,
                    canCheck: true,
                    disabled: isEmpty
                });
            }
        });

        return tagOptions;
    };

    const handleGenerateShiftSchedule = React.useCallback(async () => {
        InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingPanelGenerateClicked);
        // If start/end not set, we should show errors
        const teamId = TeamStore().teamId;
        if (!startDate || !endDate || !teamId) {
            return;
        }

        // Validate that a group is selected
        const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;
        if (!selectedGroupId) {
            setShowNoGroupDialog(true);
            return;
        }

        // Validate that the selected group has members
        if (TagUtils.isTagEmpty(selectedGroupId)) {
            setShowEmptyGroupDialog(true);
            return;
        }

        // Validate that shift blocks are created
        const shiftBlocks = scheduleGenerationEditorState().shiftBlocks;
        if (!shiftBlocks || shiftBlocks.length === 0) {
            setShowNoShiftBlocksDialog(true);
            return;
        }
        const existingShiftBlocks  = shiftBlocks.filter(shift => shift.eTag !== undefined && shift.eTag !== null);
        const newShiftBlocks = shiftBlocks.filter(shift => shift.eTag === undefined || shift.eTag === null);

        // Disable the generate button during async operations
        setIsGenerating(true);

        try {
            const pollingResult = await SmartScheduleGenerationPollingService.pollNow(teamId);
            if (pollingResult?.jobId && ![JobStatus.Failure, JobStatus.Canceled].includes(pollingResult.status)) {
                // Existing non-failure job detected (in progress or terminal success/partial);
                // dismiss panel and skip starting a new generation.
                if (!pollingResult.jobProperties?.isReviewed) {
                    closePanel();
                    setIsGenerating(false);
                    return;
                }
            }
        } catch {
            /* swallow poll errors; proceed with normal flow */
            setIsGenerating(false);
        }

        try {
            const startOfStartDate = DateUtils.getStartOfDay(startDate);
            const endOfEndDate = DateUtils.getEndOfDayForDate(endDate);
            const data = await TeamDataService.getDataInDateRange(teamId, startOfStartDate, endOfEndDate, true /* forceLoadFromNetwork */);
            const shifts = data && data.shifts ? data.shifts : [];
            const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;

            // Filter to only shifts belonging to the currently selected group (tag)
            const groupShifts = shifts.filter(shift => ShiftUtils.getTagIdFromShift(shift) === selectedGroupId);

            // Determine if there exists any non-time-off shifts within the selected group only
            const hasNonTimeOffShiftInGroup = groupShifts.some(shift => !ShiftUtils.isTimeOffEvent(shift));

            if (hasNonTimeOffShiftInGroup) {
                const nonTimeOffShifts = groupShifts.filter(shift => !ShiftUtils.isTimeOffEvent(shift));
                setExistingShiftsCount(nonTimeOffShifts.length);
                setShowExistingShiftsDialog(true);
                setIsGenerating(false);
            } else {
                try {
                    await executeScheduleGeneration();
                    setIsGenerating(false);
                } catch {
                    updateFailureStatus(teamId, scheduleGenerationEditorState().selectedGroupId, "GenerationInvokeFailed", "execute generation failed", moment(startDate).format("YYYY-MM-DD"), moment(endDate).format("YYYY-MM-DD"));
                    setIsGenerating(false);
                }

            }
            InstrumentationService.logEvent(
                InstrumentationService.events.SmartSchedulingPanelGenerateSucceeded,
                [
                    getGenericEventPropertiesObject(InstrumentationService.properties.ShiftBlocksCount, existingShiftBlocks.length),
                    getGenericEventPropertiesObject(InstrumentationService.properties.NewShiftBlocksToSaveOrDiscardCount, newShiftBlocks.length),
                    getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, selectedGroupId)
                ]
            );
        } catch (err) {
             InstrumentationService.logEvent(
                InstrumentationService.events.SmartSchedulingPanelGenerateFailed,
                [
                    getGenericEventPropertiesObject(InstrumentationService.properties.SchedulingTagId, selectedGroupId)
                ]
            );
            // Pre-check error; report failure state and still show dialog so user can proceed
            updateFailureStatus(teamId, scheduleGenerationEditorState().selectedGroupId, "PreCheckError", (err as any)?.message || "pre-check failed", moment(startDate).format("YYYY-MM-DD"), moment(endDate).format("YYYY-MM-DD"));
            setIsGenerating(false);
        }
    }, [startDate, endDate, executeScheduleGeneration, updateFailureStatus]);

    const onClickAddShift = () => {
        launchShiftEditor(
            {} as IShiftEditorData, // shiftEditorData
            ShiftTypes.SmartSchedule,
            null, // member
            scheduleGenerationEditorState().selectedGroupId || "", // groupTagId
            ScheduleCalendarTypes.Day,
            false, // isEditShift
            false, // isEditTimeoff
            shiftEditorViewState(),
            startDate,
            false, // isOpenShift
            true // isSmartScheduleshift
        );
    };

    // Hook must be called unconditionally at component level
    const { handleKeyboardNavigation, handleShiftBlockDeleted } = useShiftBlockKeyboardNavigation(addShiftBlockButtonId);

    const _renderShiftBlocks = (): JSX.Element => {
        const existingShiftBlocks  = shiftBlocks.filter(shift => shift.eTag !== undefined && shift.eTag !== null);
        const newShiftBlocks = shiftBlocks.filter(shift => shift.eTag === undefined || shift.eTag === null);

        const allShiftBlocksInOrder = [...existingShiftBlocks, ...newShiftBlocks];
        const teachingTargetShiftId = allShiftBlocksInOrder.length > 0 ? allShiftBlocksInOrder[0].id : undefined;

        return (
            <div className={pageStyles.shiftBlocksGroupContainer} onKeyDown={handleKeyboardNavigation}>
                <FluentProvider className={styles.fluentProvider} theme={ThemeUtils.getFluentV2ShiftTheme()}>
                    {
                        existingShiftBlocks.map(shift => {
                            // Use stable key based on shift ID only to prevent unnecessary remounting
                            // when slot values or other properties change
                            return <ShiftBlock
                                key={shift.id}
                                shift={shift}
                                onDeleted={handleShiftBlockDeleted}
                                showTeachingPopover={showShiftBlocksTeachingPopover && teachingTargetShiftId === shift.id}
                                onTeachingPopoverDismiss={handleShiftBlocksTeachingPopoverDismiss}
                        />;
                        })
                    }
                    {
                        existingShiftBlocks.length > 0 && newShiftBlocks.length > 0 &&
                        <Divider className={styles.shiftBlockDivider}>
                            {commonStrings.get("new")}
                        </Divider>
                    }
                    {
                        newShiftBlocks.map(shift => {
                            return <ShiftBlock
                                key={shift.id}
                                shift={shift}
                                onDeleted={handleShiftBlockDeleted}
                                showTeachingPopover={showShiftBlocksTeachingPopover && teachingTargetShiftId === shift.id}
                                onTeachingPopoverDismiss={handleShiftBlocksTeachingPopoverDismiss}
                        />;
                        })
                    }
                    <FluentButton
                        ref={addShiftBlockButtonRef}
                        id={addShiftBlockButtonId}
                        icon={<Add20Regular />}
                        onClick={onClickAddShift}
                        className={mergeClasses(pageStyles.addShiftButtonText, pageStyles.addShiftButton)}
                        size="medium">
                        <Body1 className={pageStyles.addShiftButtonText}>{strings.get("addShiftBlock")}</Body1>
                    </FluentButton>
                </FluentProvider>
            </div>
        );
    };

    const getSelectedGroupName = (): string => {
        const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;
        let tag = TagStore().tags.get(selectedGroupId || "");
        return tag ? (tag.name || commonStrings.get("unnamedGroup")) : "";
    };

   const _renderScheduleScope = (): JSX.Element => {
        const options = _getTagOptions();
        const hasOptions = options && options.length > 0;

        // Set first group as default if none is selected and options exist
        const selectedGroupId = scheduleGenerationEditorState().selectedGroupId;
        React.useEffect(() => {
            if (!selectedGroupId && hasOptions && options.length > 0) {
                const firstNonDisabledOption = options.find(opt => !opt.disabled);
                if (firstNonDisabledOption) {
                    onSelectSmartScheduleGroup({ groupId: firstNonDisabledOption.key as string });
                }
            }
        }, [selectedGroupId, hasOptions]);

        const renderViewSchedulingRulesLink = () => (
            <Link
                className={mergeClasses(pageStyles.link, pageStyles.viewScheduleLink)}
                id={viewSchedulingRulesLinkId}
                ref={viewSchedulingRulesLinkRef}
                onClick={onViewSchedulingRulesClick}>
                {strings.get("viewSchedulingRules")}
            </Link>
        );
        const localizedLink = `https://support.microsoft.com/${currentLocale}/office/set-your-availability-in-shifts-22c27e36-e392-4326-bd06-682032949eed`;
        const selectedGroupName = getSelectedGroupName();
        return (
            <div className={pageStyles.scheduleScopeContainer}>
                <Body1 className={mergeClasses(pageStyles.paragraphs, pageStyles.scheduleScopeBlock)}>
                    {strings.get("scheduleScopeInstroduction")}

                    <Link href={localizedLink} className={pageStyles.link} target="_blank" rel="noopener noreferrer">
                        {strings.get("employeeAvailability")}
                        <OpenRegular className={pageStyles.externalLinkIcon} />
                    </Link>

                    {strings.get("otherRules")}
                </Body1>
                <Body1>{renderViewSchedulingRulesLink()}</Body1>
                <Divider className={pageStyles.divider} />
                <Subtitle2 as="h2" className={pageStyles.scheduleScopeTitle} {...generateDomPropertiesForAria({ role: "heading", level: 2 })}>{strings.get("scheduleScope")}</Subtitle2>
                <Body1 className={pageStyles.scheduleUpToTime}>{strings.get("scheduleUpToTime")}</Body1>
                <div className={pageStyles.scopeFields}>
                    <Field
                        id="groupDropdownField"
                        label={strings.get("selectScheduleGroup")}>
                        <Combobox
                            id="groupDropdown"
                            value={isUserTyping ? (comboboxInputValue || "") : (selectedGroupName || "")}
                            className={pageStyles.groupDropdownBox}
                            placeholder={strings.get("selectScheduleGroup")}
                            aria-label={strings.get("selectScheduleGroup")}
                            onOptionSelect={(_, data: OptionOnSelectData) => {
                                if (data.optionValue) {
                                    const selectedKey = data.optionValue as string;
                                    onSelectSmartScheduleGroup({ groupId: selectedKey });
                                    setComboboxInputValue("");
                                    setIsUserTyping(false);
                                }
                            }}
                            onChange={(e) => {
                                try {
                                    const newValue = (e.target as HTMLInputElement)?.value ?? "";
                                    setComboboxInputValue(newValue);
                                    setIsUserTyping(true);
                                } catch (err) {
                                    console.error("Error in combobox onChange:", err);
                                    setComboboxInputValue("");
                                    setIsUserTyping(true);
                                }
                            }}
                            freeform>
                            {hasOptions && options ? options
                                .filter(option => {
                                    // If user is typing, filter options based on input
                                    if (isUserTyping && comboboxInputValue) {
                                        return option.text?.toLowerCase().includes(comboboxInputValue.toLowerCase());
                                    }
                                    // Otherwise show all options
                                    return true;
                                })
                                .map(option => {
                                    const optionElement = (
                                        <Option
                                            key={option.key}
                                            text={option.text}
                                            aria-label={option.text}
                                            value={option.key}
                                            disabled={option.disabled}
                                        >
                                            {option.text}
                                        </Option>
                                    );

                                    if (option.disabled) {
                                        return (
                                            <Tooltip
                                                key={option.key}
                                                content={strings.get("emptyGroupDropdownTooltip")}
                                                relationship="label"
                                            >
                                                {optionElement}
                                            </Tooltip>
                                        );
                                    }

                                    return optionElement;
                                }) : (
                                <Option key="no-options" value="">
                                    {strings.get("noGroupsAvailable")}
                                </Option>
                            )}
                        </Combobox>
                    </Field>
                    <Field
                        id="startDatePicker"
                        label={commonStrings.get("startDate")}>
                        <LocalizedDatePickerV2
                            value={startDate}
                            timeZoneOlsonCode={TeamSettingsStore().timeZoneOlsonCode.value}
                            onDateSelectedCallBack={(date) => handleStartDateChange(date.toDate())}
                            ariaLabelDate={commonStrings.get("startDate")}
                            datePickerClassName={pageStyles.scheduleField}
                            minDate={todayDate}
                        />
                    </Field>
                    <Field
                        id="endDatePicker"
                        label={commonStrings.get("endDate")}>
                        <div ref={endDatePickerFieldRef}>
                            <LocalizedDatePickerV2
                                value={endDate}
                                timeZoneOlsonCode={TeamSettingsStore().timeZoneOlsonCode.value}
                                onDateSelectedCallBack={(date) => handleEndDateChange(date.toDate())}
                                ariaLabelDate={commonStrings.get("endDate")}
                                datePickerClassName={pageStyles.scheduleField}
                                minDate={todayDate}
                            />
                        </div>
                    </Field>
                </div>
                <Divider className={pageStyles.divider} />
                {!isUserTyping && (
                    <div className={pageStyles.shiftBlocksContainer}>
                        <Subtitle2 as="h2" {...generateDomPropertiesForAria({ role: "heading", level: 2 })} className={mergeClasses(pageStyles.title, pageStyles.scopeTitle)}>{strings.get("shiftBlocks")}</Subtitle2>
                        <Body1 className={mergeClasses(pageStyles.paragraphs, pageStyles.shiftBlocksDescription)}>{strings.get("shiftBlocksDescription")}</Body1>
                        {_renderShiftBlocks()}
                    </div>
                )}
            </div>
        );
    };

    const handleGenerateOptionChange: NonNullable<RadioGroupProps["onChange"]> = (_, data) => {
        setGenerateOption(data.value);
    };

    const handleDismissGenerateDialog = React.useCallback(() => {
        setShowExistingShiftsDialog(false);
    }, [setShowExistingShiftsDialog]);

    const _renderNoGroupDialog = (): React.ReactNode => {
        return (
            <ShiftsDialog
                title={strings.get("noGroupSelectedTitle")}
                subText={strings.get("pleaseSelectGroup")}
                isHidden={!showNoGroupDialog}
                onDismissCallback={() => setShowNoGroupDialog(false)}
                onOkCallback={() => setShowNoGroupDialog(false)}
                hideCancelButton={true}
                okText={commonStrings.get("ok")}
            />
        );
    };

    const _renderEmptyGroupDialog = (): React.ReactNode => {
        const emptyGroupTitle = strings.get("emptyGroupTitle");
        const emptyGroupMessage = strings.get("emptyGroupMessage");
        return (
            <ShiftsDialog
                title={emptyGroupTitle}
                subText={emptyGroupMessage}
                isHidden={!showEmptyGroupDialog}
                onDismissCallback={() => setShowEmptyGroupDialog(false)}
                onOkCallback={() => setShowEmptyGroupDialog(false)}
                hideCancelButton={true}
                okText={commonStrings.get("ok")}
            />
        );
    };

    const _renderNoShiftBlocksDialog = (): React.ReactNode => {
        return (
            <ShiftsDialog
                title={strings.get("noShiftBlocksTitle")}
                subText={strings.get("pleaseAddShiftBlocks")}
                isHidden={!showNoShiftBlocksDialog}
                onDismissCallback={() => setShowNoShiftBlocksDialog(false)}
                onOkCallback={() => setShowNoShiftBlocksDialog(false)}
                hideCancelButton={true}
                okText={commonStrings.get("ok")}
            />
        );
    };

    const _renderUnsavedChangesDialog = (): React.ReactNode => {
        return (
            <NewShiftsDialog
                title={commonStrings.get("discardChanges")}
                isOpen={showUnsavedChangesDialog}
                onDismiss={() => setShowUnsavedChangesDialog(false)}
                content={
                    <div className={styles.unsavedChangesDialogContent}>
                        {commonStrings.get("unsavedChangesMessage")}
                    </div>
                }
                actions={
                    <div className={styles.generateDialogActions}>
                        <FluentButton
                            appearance="secondary"
                            onClick={handleDiscardAndClose}>
                            {commonStrings.get("discardEdits")}
                        </FluentButton>
                        <FluentButton
                            appearance="primary"
                            onClick={handleSaveChangesAndClose}>
                            {commonStrings.get("saveChanges")}
                        </FluentButton>
                    </div>
                }
                customStyles={{ dialogBodyContent: styles.editorDialogBodyContent }}
                showDismiss={true}
            />
        );
    };

    const _renderBeginOnboardingDialog = (): React.ReactNode => {
        if (showBeginOnboardingDialog && !hasLoggedBeginOnboardingDialogShownRef.current) {
            InstrumentationService.logEvent(InstrumentationService.events.SmartSchedulingFreBeginOnboardingDialogShown);
            hasLoggedBeginOnboardingDialogShownRef.current = true;
        } else if (!showBeginOnboardingDialog && hasLoggedBeginOnboardingDialogShownRef.current) {
            hasLoggedBeginOnboardingDialogShownRef.current = false;
        }

        const titleText = strings.get("smartSchedulingMadeEasyPopoverTitle");
        const contentText = strings.get("smartSchedulingMadeEasyPopoverContent");
        const smartScheduleImage = require("sh-assets/images/smart-scheduling2.svg");

        const dialogContent = (
            <div className={styles.madeEasyDialog}>
                <img
                    src={smartScheduleImage}
                    className={styles.madeEasyImage}
                    alt={titleText}
                />
                <div className={styles.madeEasyTextContainer}>
                    <Subtitle1>{titleText}</Subtitle1>
                    <Body1>{contentText}</Body1>
                </div>
            </div>
        );

        const dialogActions = (
            <div className={styles.madeEasyDialogActions}>
                <FluentButton appearance="secondary" onClick={handleBeginOnboardingSkip}>
                    {teamSetupStartTourStrings.get("skipButtonLabel")}
                </FluentButton>
                <FluentButton
                    appearance="primary"
                    onClick={handleBeginOnboardingPrimaryClick}
                    ref={beginOnboardingPrimaryButtonRef}
                >
                    {strings.get("smartSchedulingMadeEasyPopoverPrimaryButton")}
                </FluentButton>
            </div>
        );

        return (
            <NewShiftsDialog
                title=""
                isOpen={showBeginOnboardingDialog}
                onDismiss={handleBeginOnboardingSkip}
                content={dialogContent}
                actions={dialogActions}
                customStyles={{
                    dialogSurface: styles.madeEasyDialogSurface,
                    dialogBody: styles.madeEasyDialogBody
                }}
                showDismiss={false}
            />
        );
    };

    const _renderTeachingPopover = (): React.ReactNode => {
        if (!shouldShowTeachingPopover || !teachingPopoverTarget) {
            return null;
        }

        const isDateRangeStep = teachingCarouselValue === TeachingCarouselStep.DateRange;
        const isSchedulingRulesStep = teachingCarouselValue === TeachingCarouselStep.SchedulingRules;
        const isShiftBlocksStep = teachingCarouselValue === TeachingCarouselStep.ShiftBlocks;
        const previousButtonSlot: TeachingPopoverCarouselFooterButtonProps | null = isDateRangeStep
            ? null
            : {
                navType: "prev",
                altText: teamSetupPickerStrings.get("backButtonLabel"),
                as: "button",
                onClick: () => {
                    if (isShiftBlocksStep) {
                        moveToSchedulingRulesStep();
                    } else {
                        moveToDateRangeStep();
                    }
                },
                children: teamSetupPickerStrings.get("backButtonLabel")
            };

        const nextButtonSlot: TeachingPopoverCarouselFooterButtonProps = isDateRangeStep
            ? {
                navType: "next",
                altText: commonStrings.get("next"),
                as: "button",
                onClick: () => {
                    moveToSchedulingRulesStep();
                },
                children: commonStrings.get("next")
            }
            : isSchedulingRulesStep
                ? {
                    navType: "next",
                    altText: commonStrings.get("gotIt"),
                    as: "button",
                    onClick: () => {
                        moveToShiftBlockStep();
                    },
                    children: commonStrings.get("next")
                }
                : {
                    navType: "next",
                    altText: commonStrings.get("gotIt"),
                    as: "button",
                    onClick: () => {
                        completeTeachingSteps();
                    },
                    children: commonStrings.get("gotIt")
                };

        return (
            <FluentProvider className={styles.teachingPopover} theme={ThemeUtils.getFluentV2ShiftTheme()}>
                <TeachingPopover
                    open={shouldShowTeachingPopover}
                    trapFocus={false}
                    positioning={{ position: "after", align: "start", target: teachingPopoverTarget, offset: { crossAxis: 0, mainAxis: 14 } }}
                    onOpenChange={handleTeachingPopoverOpenChange}
                >
                    <TeachingPopoverSurface>
                        <TeachingPopoverCarousel
                            value={teachingCarouselValue}
                            onValueChange={handleTeachingCarouselValueChange}
                        >
                            <TeachingPopoverCarouselCard value={TeachingCarouselStep.DateRange}>
                                <TeachingPopoverBody>
                                    <TeachingPopoverTitle>{strings.get("teachingBubbleDateRangeTitle")}</TeachingPopoverTitle>
                                    <span className={styles["teaching-bubble-text"]}>
                                        {strings.get("teachingBubbleDateRangeContent")}
                                    </span>
                                </TeachingPopoverBody>
                            </TeachingPopoverCarouselCard>
                            <TeachingPopoverCarouselCard value={TeachingCarouselStep.SchedulingRules}>
                                <TeachingPopoverBody>
                                    <TeachingPopoverTitle>{strings.get("teachingBubbleSchedulingRulesTitle")}</TeachingPopoverTitle>
                                    <span className={styles["teaching-bubble-text"]}>
                                        {strings.get("teachingBubbleSchedulingRulesContent")}
                                    </span>
                                </TeachingPopoverBody>
                            </TeachingPopoverCarouselCard>
                            <TeachingPopoverCarouselCard value={TeachingCarouselStep.ShiftBlocks}>
                                <TeachingPopoverBody>
                                    <TeachingPopoverTitle>{strings.get("teachingBubbleShiftBlockTitle")}</TeachingPopoverTitle>
                                    <span className={styles["teaching-bubble-text"]}>
                                        {strings.get("teachingBubbleShiftBlockContent")}
                                    </span>
                                </TeachingPopoverBody>
                            </TeachingPopoverCarouselCard>
                            <TeachingPopoverCarouselFooter
                                previous={previousButtonSlot as any}
                                next={nextButtonSlot as any}
                                initialStepText={""}
                                finalStepText={commonStrings.get("gotIt")}
                            >
                                <TeachingPopoverCarouselNav>
                                    {(value: string) => (
                                        <TeachingPopoverCarouselNavButton
                                            aria-label={value === TeachingCarouselStep.ShiftBlocks
                                                ? strings.get("teachingBubbleShiftBlockTitle")
                                                : value === TeachingCarouselStep.SchedulingRules
                                                    ? strings.get("teachingBubbleSchedulingRulesTitle")
                                                    : strings.get("teachingBubbleDateRangeTitle")}
                                        />
                                    )}
                                </TeachingPopoverCarouselNav>
                            </TeachingPopoverCarouselFooter>
                        </TeachingPopoverCarousel>
                    </TeachingPopoverSurface>
                </TeachingPopover>
            </FluentProvider>
        );
    };

    const _renderSmartSchedulingPanel = (): React.ReactNode => {
        const callbacks = {
            navigateToSettings: () => {
                // Navigate to team settings scheduling rules section using proper routing
                setGlobalCommandBarSelectedTab(GlobalCommandBarTab.Settings);
                const settingsUrl = UrlFactory.getTeamSettingsUrl(TeamStore().teamId);
                history.push(settingsUrl + "#schedulingSettings");
                schedulingConstraintsPanelActions.closePanel();
            }
        };

        return (
            <CallbacksProvider value={callbacks}>
                <SchedulingRulesPanel theme={ThemeUtils.getFluentV2ShiftTheme()} />
            </CallbacksProvider>
        );
    };

    const selectedGroupNameForDialog = getSelectedGroupName() || commonStrings.get("selectScheduleGroup");

    return scheduleGenerationEditorState().isAutoSchedulePanelOpen &&
                (<FocusTrapZone
                    isClickableOutsideFocusTrap={false}
                    forceFocusInsideTrap={true}
                    disableFirstFocus={false}
                >
                    <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()}>
                        <div className={`${styles.panel} ${styles.autoScheduleContainer}`}>
                            <Sticky
                                innerZ={STICKY_Z_INDEX + 1}
                                enableTransforms={true}>
                                <ScheduleGenerationEditorPageHeader
                                    onSaveAndClose={handleSaveAndClose}
                                    onGenerate={handleGenerateShiftSchedule}
                                    onClose={closePanel}
                                    isGenerating={isGenerating}
                                    hasValidGroup={!isUserTyping && !!scheduleGenerationEditorState().selectedGroupId}
                                    saveAndCloseButtonRef={saveAndCloseButtonRef}
                                />
                                <Divider />
                            </Sticky>
                            {_renderScheduleScope()}
                            {isCurrentUserAdmin && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) && _renderBeginOnboardingDialog()}
                            {isCurrentUserAdmin && ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableSmartScheduling) && (
                                <ExistingShiftsDialog
                                    isOpen={showExistingShiftsDialog}
                                    selectedGroupName={selectedGroupNameForDialog}
                                    startDate={startDate}
                                    endDate={endDate}
                                    existingShiftsCount={existingShiftsCount}
                                    generateOption={generateOption}
                                    onGenerateOptionChange={handleGenerateOptionChange}
                                    onDismiss={handleDismissGenerateDialog}
                                    onGenerate={executeScheduleGeneration}
                                    strings={strings}
                                    commonStrings={commonStrings}
                                    scheduleStrings={scheduleStrings}
                                    pageStyles={pageStyles}
                                    currentLocale={currentLocale}
                                />
                            )}
                        </div>
                    </FluentProvider>
                {_renderSmartSchedulingPanel()}
                {_renderNoGroupDialog()}
                {_renderEmptyGroupDialog()}
                {_renderNoShiftBlocksDialog()}
                {_renderTeachingPopover()}
            {_renderUnsavedChangesDialog()}
            </FocusTrapZone>);
};

export default observer(ShiftGenerationEditorPage);